import axios from "axios";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { useAxiosHook } from "./useAxiosHook";

const ROUTE_PREFIX = "borrower_submissions";
// The redemption half is anonymous — nothing under this prefix may assume a session.
const PUBLIC_ROUTE_PREFIX = "public";

export const LINK_KIND_APPLICATION = "application";
export const LINK_KIND_CREDIT_AUTHORIZATION = "credit_authorization";

/**
 * Application-link subsystem.
 *
 * Contract: lendmarq-os `frontend_documentation/application-links-and-external-
 * submissions.md`. Read it before changing anything here — the payload keys and
 * response shapes below are that document, not a guess.
 *
 * Two things about this flow that are easy to get wrong:
 *
 *  1. `sendLinks` mints BOTH kinds in ONE call via the `kinds` array, and the
 *     backend emails the client itself. Do not loop per kind.
 *  2. The raw `url` on a TARGETED link is returned EXACTLY ONCE. The token is
 *     stored only as a sha256, and the persisted `link_url` is a display-safe
 *     `?t=<prefix>…`. If the response is dropped, that link can never be
 *     recovered — only re-minted. Surface it immediately.
 *
 * `createDedicatedLink` / `getDedicatedLinks` are the exception to BOTH: one
 * kind per call, and their URL is permanent and re-readable because it is meant
 * to live in an email signature. See their own notes below.
 */
export const useApplicationLinksHook = () => {
  const {
    axiosGetRequest,
    axiosPostRequest,
    axiosGetIsLoading,
    axiosPostIsLoading,
    axiosError,
  } = useAxiosHook();

  const loading = axiosGetIsLoading || axiosPostIsLoading;
  const error = axiosError;

  // -------------------------------------
  //  POST /borrower_submissions/links/send
  // -------------------------------------
  /**
   * Mint one link per requested kind and email them to the client.
   *
   * Internal staff only (admin or pipeline access) — anyone else gets 403.
   * A denied-PII prefill field returns 400 with the reason, deliberately loud;
   * unknown fields are dropped silently and reported on the link row.
   *
   * Resolves with `{ recipient_email, dealId, email_sent, email_error, links[] }`
   * where each link is `{ link_id, link_kind, token_prefix, expires_at, url }`.
   */
  const sendLinks = async ({
    recipientEmail,
    recipientName,
    kinds,
    prefill,
    note,
    dealId,
    ownerEmail,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(recipientEmail) || isEmpty(recipientEmail)) {
      console.error("Recipient email is required", { recipientEmail });
      if (onFailFn) onFailFn(new Error("Recipient email is required"));
      return;
    }
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Send Application Links",
      path: `${ROUTE_PREFIX}/links/send`,
      payload: {
        recipient_email: recipientEmail,
        recipient_name: recipientName || undefined,
        kinds: kinds && kinds.length ? kinds : [LINK_KIND_APPLICATION],
        prefill: prefill && Object.keys(prefill).length ? prefill : undefined,
        note: note || undefined,
        dealId: dealId || undefined,
        owner_email: ownerEmail || undefined,
      },
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  POST /borrower_submissions/links/dedicated
  // -------------------------------------
  /**
   * The LO's DEDICATED (evergreen) link — the one they paste somewhere and
   * leave, rather than send to one borrower.
   *
   * Three ways it differs from `sendLinks`, all of them load-bearing:
   *   1. No email is sent, and there is no recipient at all.
   *   2. No prefill. It is reusable by many borrowers, so there is nobody to
   *      prefill it FOR — the server stores `prefill: {}` and ignores the key.
   *   3. It is get-or-create per (owner, kind) and NEVER expires. Calling it
   *      again returns the SAME url, so it is safe to call on page load and the
   *      URL can go in an email signature without going stale.
   *
   * Attribution is unchanged: `owner_user_id` is stamped on the link, so every
   * submission through it routes back to that LO exactly as a targeted link does.
   *
   * Resolves with `{ link_id, link_kind, link_mode: "evergreen", url,
   * owner_user_id, owner_email, expires_at: null, stats }`.
   */
  const createDedicatedLink = async ({
    kind,
    ownerEmail,
    sentOnBehalfOf,
    permittedUsers,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Dedicated Application Link",
      path: `${ROUTE_PREFIX}/links/dedicated`,
      payload: {
        kind: kind || LINK_KIND_APPLICATION,
        owner_email: ownerEmail || undefined,
        sent_on_behalf_of: sentOnBehalfOf || undefined,
        permitted_users:
          permittedUsers && permittedUsers.length ? permittedUsers : undefined,
      },
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  GET /borrower_submissions/links/dedicated
  // -------------------------------------
  /**
   * Read side of the same get-or-create: the caller's (or `ownerEmail`'s)
   * dedicated links. Omit `kind` for BOTH kinds in one call — that is the shape
   * a profile page wants, so it can render an application URL and a credit-auth
   * URL side by side without minting churn.
   *
   * Resolves with `{ owner_email, links: DedicatedLink[] }`.
   */
  const getDedicatedLinks = async ({
    kind,
    ownerEmail,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  } = {}) => {
    const query = new URLSearchParams();
    if (kind) query.set("kind", kind);
    if (ownerEmail) query.set("owner_email", ownerEmail);

    const source = axios.CancelToken.source();
    await axiosGetRequest({
      name: "Get Dedicated Application Links",
      path: `${ROUTE_PREFIX}/links/dedicated${
        query.toString() ? `?${query}` : ""
      }`,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  GET /borrower_submissions/links
  // -------------------------------------
  /** Internal staff see all; everyone else only links they own. */
  const getLinks = async ({
    kind,
    status,
    recipientEmail,
    dealId,
    limit,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const query = new URLSearchParams();
    if (kind) query.set("kind", kind);
    if (status) query.set("status", status);
    if (recipientEmail) query.set("recipient_email", recipientEmail);
    if (dealId) query.set("dealId", dealId);
    if (limit) query.set("limit", String(limit));

    const source = axios.CancelToken.source();
    await axiosGetRequest({
      name: "Get Application Links",
      path: `${ROUTE_PREFIX}/links${query.toString() ? `?${query}` : ""}`,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  POST /borrower_submissions/links/{link_id}/revoke
  // -------------------------------------
  /** 404 when the link is not active. Internal staff only. */
  const revokeLink = async ({ linkId, onSuccessFn, onCompleteFn, onFailFn }) => {
    if (isNil(linkId) || isEmpty(linkId)) {
      console.error("Link id is missing", { linkId });
      return;
    }
    const source = axios.CancelToken.source();
    await axiosPostRequest({
      name: "Revoke Application Link",
      path: `${ROUTE_PREFIX}/links/${linkId}/revoke`,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  GET /public/link/resolve
  // -------------------------------------
  /**
   * Anonymous. Accepts either generation of link:
   *   - new:    ?t=<token>
   *   - legacy: ?link_id=<oid>&link_type=app|auth   (985 of these are in the wild)
   *
   * 404 = unknown link. A 200 always carries `status`; only render the form when
   * it is "ok" — `prefill` is null for every other status.
   *
   * `hideFailMsg`: a dead link is a page state, not a snackbar.
   */
  const resolveLink = async ({
    token,
    linkId,
    linkType,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const query = new URLSearchParams();
    if (token) query.set("t", token);
    else if (linkId) {
      query.set("link_id", linkId);
      if (linkType) query.set("link_type", linkType);
    } else {
      console.error("resolveLink needs a token or a legacy link id");
      return;
    }

    const source = axios.CancelToken.source();
    await axiosGetRequest({
      name: "Resolve Application Link",
      path: `${PUBLIC_ROUTE_PREFIX}/link/resolve?${query}`,
      cancelSource: source,
      hideFailMsg: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // -------------------------------------
  //  POST /public/link/opened | /public/link/started
  // -------------------------------------
  /**
   * Fire-and-forget funnel beacons. Always 200 server-side; never let one block
   * or fail the borrower's form. `opened` is deduped per (link, session_key) per
   * 24h, so the session key must be stable for the visit.
   */
  const trackLinkEvent = async ({ event, token, linkId, sessionKey }) => {
    if (event !== "opened" && event !== "started") return;
    if (!token && !linkId) return;

    await axiosPostRequest({
      name: `Track Link ${event}`,
      path: `${PUBLIC_ROUTE_PREFIX}/link/${event}`,
      payload: {
        ...(token ? { t: token } : { link_id: linkId }),
        session_key: sessionKey,
      },
      hideFailMsg: true,
      onFailFn: () => {},
    });
  };

  return {
    loading,
    error,
    sendLinks,
    createDedicatedLink,
    getDedicatedLinks,
    getLinks,
    revokeLink,
    resolveLink,
    trackLinkEvent,
  };
};

export default useApplicationLinksHook;
